import React from 'react';
import { GoogleMap, MarkerF, useJsApiLoader } from '@react-google-maps/api';
import NearbyPlaceRow from './NearbyPlaceRow';
import { RichPayload } from './contentCardUtils';
import type {
  FindLocationPayload,
  NearbyPlace,
} from '../../../data-access/api/science/openapi/api';
import './richContext.scss';

type Props = {
  p: RichPayload | FindLocationPayload;
  enrichmentPending: boolean;
};

const FindLocationContent: React.FC<Props> = ({ p, enrichmentPending }) => {
  const query =
    (typeof p.query === 'string' && p.query) ||
    (typeof p.label === 'string' && p.label) ||
    (typeof p.title === 'string' && p.title) ||
    (typeof p.name === 'string' && p.name) ||
    '';
  const nearbyPlaces = Array.isArray(p.nearby_places) ? (p.nearby_places as NearbyPlace[]) : [];
  const hasPins = nearbyPlaces.length > 0;

  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? '';
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'lifestack-google-maps-script',
    googleMapsApiKey,
  });

  const mapCenter = React.useMemo(() => {
    const first = nearbyPlaces[0];
    if (first && Number.isFinite(first.lat) && Number.isFinite(first.lng)) {
      return { lat: first.lat, lng: first.lng };
    }
    return { lat: 0, lng: 0 };
  }, [nearbyPlaces]);

  const onMapLoad = React.useCallback(
    (map: google.maps.Map) => {
      if (!nearbyPlaces.length) return;
      const bounds = new google.maps.LatLngBounds();
      for (const place of nearbyPlaces) {
        if (Number.isFinite(place.lat) && Number.isFinite(place.lng)) {
          bounds.extend({ lat: place.lat, lng: place.lng });
        }
      }
      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, 48);
      }
    },
    [nearbyPlaces]
  );

  return (
    <div className="rc-content-body">
      {!!query && <span className="rc-content-label">{query}</span>}
      {hasPins && !!googleMapsApiKey && isLoaded && !loadError ? (
        <div className="rc-map-embed" aria-label="Map preview">
          <GoogleMap
            mapContainerClassName="rc-map-embed-inner"
            center={mapCenter}
            zoom={13}
            onLoad={onMapLoad}
            options={{
              disableDefaultUI: true,
              clickableIcons: false,
              gestureHandling: 'cooperative',
              zoomControl: true,
              fullscreenControl: false,
              mapTypeControl: false,
              streetViewControl: false,
            }}
          >
            {nearbyPlaces.map((place, i) => (
              <MarkerF
                key={place.place_id ?? `${place.lat},${place.lng},${i}`}
                position={{ lat: place.lat, lng: place.lng }}
                title={place.name}
                label={{
                  text: String(i + 1),
                  color: '#ffffff',
                  fontSize: '11px',
                  fontWeight: '700',
                }}
              />
            ))}
          </GoogleMap>
        </div>
      ) : null}
      {hasPins ? (
        <div className="rc-nearby-list">
          {nearbyPlaces.map((place, i) => (
            <NearbyPlaceRow key={i} place={place} index={i + 1} />
          ))}
        </div>
      ) : enrichmentPending ? (
        <div className="rc-skeleton-group">
          <div className="rc-skeleton-row" />
          <div className="rc-skeleton-row rc-skeleton-row--short" />
          <div className="rc-skeleton-row" />
        </div>
      ) : null}
    </div>
  );
};

export default FindLocationContent;
